import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type Announcement, selectPendingAnnouncements } from "../domain/announcement";
import { ANNOUNCEMENTS } from "../domain/catalog";
import { AnnouncementsAPI } from "../infrastructure/api/announcements.api";

const ACKNOWLEDGEMENTS_KEY = ["announcement-acknowledgements"];

export interface PendingAnnouncements {
    pending: Announcement[];
    /** False only while an enabled lookup has not answered yet. */
    isSettled: boolean;
}

/**
 * The announcements the current therapist has not seen yet.
 *
 * Asks the server only when the catalogue has something live, so an empty
 * catalogue costs no request. A failed lookup settles with nothing pending:
 * news is never worth blocking the app for.
 */
export const usePendingAnnouncements = (enabled: boolean): PendingAnnouncements => {
    const now = useMemo(() => new Date(), []);
    const hasLiveAnnouncements = selectPendingAnnouncements(ANNOUNCEMENTS, [], now).length > 0;
    const isEnabled = enabled && hasLiveAnnouncements;

    const query = useQuery({
        queryKey: ACKNOWLEDGEMENTS_KEY,
        queryFn: async () => {
            const { data } = await AnnouncementsAPI.listAcknowledged();
            return data.announcement_ids;
        },
        enabled: isEnabled,
        // Only this session changes it, and it does so through the cache.
        staleTime: Infinity,
        retry: false,
    });

    const pending = useMemo(
        () => (query.data ? selectPendingAnnouncements(ANNOUNCEMENTS, query.data, now) : []),
        [query.data, now],
    );

    return { pending, isSettled: !isEnabled || !query.isPending };
};

/**
 * Records that the therapist has seen these announcements.
 *
 * The cache is updated first, so nothing re-opens while the request is out.
 * A failure is left in the cache on purpose: the modal stays closed for this
 * session, and with nothing saved it comes back on the next entry.
 */
export const useAcknowledgeAnnouncements = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (announcementIds: string[]) => AnnouncementsAPI.acknowledge(announcementIds),
        onMutate: (announcementIds) => {
            queryClient.setQueryData<string[]>(ACKNOWLEDGEMENTS_KEY, (current = []) =>
                Array.from(new Set([...current, ...announcementIds])),
            );
        },
        onError: (error) => {
            console.error("Failed to acknowledge announcements", error);
        },
    });
};
